import React, { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import { currentYearRange } from "../utils/payrollEftReportUtils";
import {
  DEFAULT_COMPANY,
  buildCompanyPayerBlock,
  computeNetCommissionForAgent,
  fillT4aPdfBytes,
  findLoggedInAgent,
  formatT4aAmount,
  loadT4aTemplateBytes,
  mergeT4aPdfBytes,
} from "../utils/t4aPdfFill";

function agentLabel(agent) {
  if (!agent) return "";
  const name =
    [agent.firstName, agent.lastName].filter(Boolean).join(" ").trim() ||
    agent.legalName ||
    "Unknown";
  const no = agent.employeeNo != null ? `#${agent.employeeNo}` : "";
  return no ? `${no} — ${name}` : name;
}

function normalizeCompany(companyRaw) {
  const raw = companyRaw && typeof companyRaw === "object" ? companyRaw : {};
  return {
    companyName:
      String(raw.companyName || "").trim() || DEFAULT_COMPANY.companyName,
    address: String(raw.address || "").trim() || DEFAULT_COMPANY.address,
    city: String(raw.city || "").trim() || DEFAULT_COMPANY.city,
    province: String(raw.province || "").trim() || DEFAULT_COMPANY.province,
    postalCode:
      String(raw.postalCode || "").trim() || DEFAULT_COMPANY.postalCode,
    hstNumber: String(raw.hstNumber || "").trim(),
  };
}

export default function T4APage() {
  const { appUser, isAgent, isAdmin, isSuperUser } = useRole();
  const isOfficeUser = isAdmin || isSuperUser;
  const yearRange = useMemo(() => currentYearRange(), []);

  const [bootLoading, setBootLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [agents, setAgents] = useState([]);
  const [efts, setEfts] = useState([]);
  const [company, setCompany] = useState(DEFAULT_COMPANY);
  const [payerAccount, setPayerAccount] = useState("");
  /** Office: multi-select map agentId -> checked (default all true). */
  const [selectedMap, setSelectedMap] = useState({});
  /** Agent portal: single agent id. */
  const [selfAgentId, setSelfAgentId] = useState("");
  const [postedByAgent, setPostedByAgent] = useState({});
  const [selfPosted, setSelfPosted] = useState(false);

  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfBytes, setPdfBytes] = useState(null);
  const [meta, setMeta] = useState({
    agentName: "",
    netCommission: 0,
    agentCount: 0,
  });

  const agentOptions = useMemo(() => {
    return [...agents].sort((a, b) =>
      agentLabel(a).localeCompare(agentLabel(b), undefined, {
        sensitivity: "base",
      }),
    );
  }, [agents]);

  /** Prefer agents with Total Payment in the year; else all agents. */
  const selectableAgents = useMemo(() => {
    if (!isOfficeUser) return agentOptions;
    const withPay = agentOptions.filter((agent) => {
      const amt = computeNetCommissionForAgent(
        efts,
        agents,
        agent,
        yearRange,
      );
      return amt > 0;
    });
    return withPay.length ? withPay : agentOptions;
  }, [agentOptions, agents, efts, isOfficeUser, yearRange]);

  const selectedAgents = useMemo(() => {
    if (!isOfficeUser) {
      const me = agents.find((a) => String(a._id) === String(selfAgentId));
      return me ? [me] : [];
    }
    return selectableAgents.filter((a) => selectedMap[String(a._id)]);
  }, [
    isOfficeUser,
    agents,
    selfAgentId,
    selectableAgents,
    selectedMap,
  ]);

  const clearPdf = useCallback(() => {
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    setPdfBytes(null);
  }, []);

  const loadPostedMap = useCallback(
    async (agentList) => {
      try {
        const res = await axiosInstance.get("/t4a-posts", {
          params: { year: yearRange.year },
        });
        const posts = Array.isArray(res.data) ? res.data : [];
        const map = {};
        for (const p of posts) {
          map[String(p.agentId)] = p;
        }
        // Ensure keys for listed agents
        for (const a of agentList || []) {
          const id = String(a._id);
          if (map[id] === undefined) map[id] = null;
        }
        setPostedByAgent(map);
        return map;
      } catch (_) {
        setPostedByAgent({});
        return {};
      }
    },
    [yearRange.year],
  );

  const buildCombinedPdf = useCallback(
    async (agentList, overrides = {}) => {
      if (!agentList?.length) {
        clearPdf();
        return;
      }
      setBuilding(true);
      setError("");
      try {
        const eftSource = overrides.efts ?? efts;
        const agentSource = overrides.agents ?? agents;
        const companySource = overrides.company ?? company;
        const accountSource = overrides.payerAccount ?? payerAccount;
        const template = await loadT4aTemplateBytes();

        const parts = [];
        let totalBox20 = 0;
        for (const agent of agentList) {
          const box20 = computeNetCommissionForAgent(
            eftSource,
            agentSource,
            agent,
            yearRange,
          );
          totalBox20 += box20;
          const filled = await fillT4aPdfBytes(template, {
            year: yearRange.year,
            companyBlock: buildCompanyPayerBlock(companySource),
            payerAccount: accountSource,
            agent,
            netCommission: box20,
          });
          parts.push(filled);
        }

        const combined = await mergeT4aPdfBytes(parts);
        const blob = new Blob([combined], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setPdfBytes(combined);
        setMeta({
          agentName:
            agentList.length === 1
              ? agentLabel(agentList[0]).replace(/^#\d+\s*—\s*/, "")
              : `${agentList.length} agents`,
          netCommission: Math.round(totalBox20 * 100) / 100,
          agentCount: agentList.length,
        });
      } catch (err) {
        clearPdf();
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to build T4A PDF.",
        );
      } finally {
        setBuilding(false);
      }
    },
    [agents, clearPdf, company, efts, payerAccount, yearRange],
  );

  // Boot
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBootLoading(true);
      setError("");
      setInfo("");
      try {
        const [eftRes, agentsRes, companyRes, miscRes] = await Promise.all([
          axiosInstance.get("/commission-trust-eft"),
          axiosInstance.get("/agents"),
          axiosInstance.get("/company-profile").catch(() => ({ data: null })),
          axiosInstance.get("/misc-settings").catch(() => ({ data: null })),
        ]);
        if (cancelled) return;

        const agentList = Array.isArray(agentsRes.data) ? agentsRes.data : [];
        const eftList = Array.isArray(eftRes.data) ? eftRes.data : [];
        const misc = Array.isArray(miscRes.data)
          ? miscRes.data[0]
          : miscRes.data;
        const companyRaw = Array.isArray(companyRes.data)
          ? companyRes.data[0]
          : companyRes.data;

        const companyNorm = normalizeCompany(companyRaw);
        // Prefer company profile HST # (T4A payer account); fall back to misc settings.
        const account =
          String(companyNorm.hstNumber || companyRaw?.hstNumber || "").trim() ||
          (misc?.hstNumber != null && misc?.hstNumber !== ""
            ? String(misc.hstNumber)
            : "");

        setAgents(agentList);
        setEfts(eftList);
        setCompany(companyNorm);
        setPayerAccount(account);

        if (isOfficeUser) {
          const paid = [...agentList]
            .sort((a, b) =>
              agentLabel(a).localeCompare(agentLabel(b), undefined, {
                sensitivity: "base",
              }),
            )
            .filter((agent) => {
              const amt = computeNetCommissionForAgent(
                eftList,
                agentList,
                agent,
                yearRange,
              );
              return amt > 0;
            });
          const listForSelect = paid.length ? paid : agentList;
          const map = {};
          for (const a of listForSelect) map[String(a._id)] = true;
          setSelectedMap(map);
          await loadPostedMap(listForSelect);
          if (cancelled) return;
          setInfo(
            "All agents are selected by default. Untick any you want to exclude, then Print or Post.",
          );
          await buildCombinedPdf(listForSelect, {
            efts: eftList,
            agents: agentList,
            company: companyNorm,
            payerAccount: account,
          });
        } else if (isAgent) {
          const me = findLoggedInAgent(agentList, appUser);
          if (!me) {
            throw new Error(
              "Could not match your login to an agent profile. Ask admin to link your account.",
            );
          }
          setSelfAgentId(String(me._id));
          try {
            const res = await axiosInstance.get("/t4a-posts/status", {
              params: { agentId: me._id, year: yearRange.year },
            });
            const isPosted = Boolean(res.data?.posted);
            setSelfPosted(isPosted);
            if (!isPosted) {
              clearPdf();
              setInfo(
                "Your T4A is not available yet. It will appear here after the office posts it.",
              );
            } else {
              setInfo("");
              await buildCombinedPdf([me], {
                efts: eftList,
                agents: agentList,
                company: companyNorm,
                payerAccount: account,
              });
            }
          } catch (_) {
            clearPdf();
            setInfo(
              "Your T4A is not available yet. It will appear here after the office posts it.",
            );
          }
        } else {
          throw new Error("You do not have access to T4A.");
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load T4A.",
          );
          clearPdf();
        }
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appUser, isAgent, isOfficeUser]);

  // Office: rebuild when selection changes (after boot).
  useEffect(() => {
    if (!isOfficeUser || bootLoading) return;
    if (!selectedAgents.length) {
      clearPdf();
      setInfo("Select at least one agent to generate T4A slips.");
      return;
    }
    let cancelled = false;
    (async () => {
      await buildCombinedPdf(selectedAgents);
      if (cancelled) return;
      const n = selectedAgents.length;
      const allPosted = selectedAgents.every(
        (a) => postedByAgent[String(a._id)],
      );
      setInfo(
        allPosted
          ? `${n} agent${n === 1 ? "" : "s"} selected — already posted.`
          : `${n} agent${n === 1 ? "" : "s"} selected. Print the combined PDF, or Post to release to agent portals.`,
      );
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMap, isOfficeUser, bootLoading]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const toggleAgent = (id) => {
    const key = String(id);
    setSelectedMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectAllAgents = () => {
    const next = {};
    for (const a of selectableAgents) next[String(a._id)] = true;
    setSelectedMap(next);
  };

  const clearAllAgents = () => {
    const next = {};
    for (const a of selectableAgents) next[String(a._id)] = false;
    setSelectedMap(next);
  };

  const handlePost = async () => {
    if (!isOfficeUser || !selectedAgents.length) return;
    setPosting(true);
    setError("");
    try {
      const postedBy = appUser?.email || appUser?.displayName || "";
      await Promise.all(
        selectedAgents.map((agent) =>
          axiosInstance.post("/t4a-posts", {
            agentId: agent._id,
            year: yearRange.year,
            postedBy,
          }),
        ),
      );
      await loadPostedMap(selectableAgents);
      setInfo(
        `Posted for ${selectedAgents.length} agent${
          selectedAgents.length === 1 ? "" : "s"
        }. They can now view their T4A in the agent portal.`,
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to post T4A.",
      );
    } finally {
      setPosting(false);
    }
  };

  const handleDownload = () => {
    if (!pdfBytes) return;
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const suffix =
      meta.agentCount > 1
        ? `${meta.agentCount}-agents`
        : meta.agentName || "agent";
    a.download = `T4A-${yearRange.year}-${suffix}.pdf`.replace(/\s+/g, "-");
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!pdfUrl) return;
    const w = window.open(pdfUrl, "_blank");
    if (!w) {
      setError("Pop-up blocked. Allow pop-ups to print, or use Download.");
      return;
    }
    setTimeout(() => {
      try {
        w.focus();
        w.print();
      } catch (_) {}
    }, 600);
  };

  const allSelectedPosted =
    isOfficeUser &&
    selectedAgents.length > 0 &&
    selectedAgents.every((a) => postedByAgent[String(a._id)]);

  const showPdf =
    Boolean(pdfUrl) && (isOfficeUser || selfPosted);
  const loading = bootLoading || building;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
              T4A — {yearRange.year}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Period: {yearRange.label}
              {meta.agentName ? ` · ${meta.agentName}` : ""}
            </p>
            {showPdf && !loading ? (
              <p className="mt-1 text-sm text-slate-600">
                Box 20 (Total Payment)
                {meta.agentCount > 1 ? " combined" : ""}:{" "}
                <span className="font-medium tabular-nums">
                  ${formatT4aAmount(meta.netCommission)}
                </span>
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isOfficeUser ? (
              <button
                type="button"
                onClick={handlePost}
                disabled={
                  !selectedAgents.length ||
                  posting ||
                  building ||
                  allSelectedPosted
                }
                className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                {allSelectedPosted
                  ? "Posted"
                  : posting
                    ? "Posting…"
                    : `Post${selectedAgents.length > 1 ? ` (${selectedAgents.length})` : ""}`}
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleDownload}
              disabled={!showPdf || loading}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-800 hover:bg-slate-50 disabled:opacity-50"
            >
              Download PDF
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={!showPdf || loading}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-900 text-white hover:bg-blue-800 disabled:opacity-50"
            >
              Print
              {isOfficeUser && selectedAgents.length > 1
                ? ` (${selectedAgents.length})`
                : ""}
            </button>
          </div>
        </div>

        {error ? (
          <div
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            role="alert"
          >
            {error}
          </div>
        ) : null}

        {info && !error ? (
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-700">
            {info}
          </div>
        ) : null}

        {loading && bootLoading ? (
          <div className="rounded-xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500 shadow-sm">
            Loading…
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-start">
            {isOfficeUser ? (
              <aside className="w-full lg:w-64 xl:w-72 flex-shrink-0 rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col max-h-[70vh] lg:max-h-[calc(100vh-12rem)]">
                <div className="px-3 py-3 border-b border-slate-200 flex-shrink-0">
                  <h2 className="text-sm font-semibold text-slate-800">
                    Agents — {yearRange.year}
                  </h2>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={selectAllAgents}
                      className="text-xs font-medium text-blue-900 hover:underline"
                    >
                      Select all
                    </button>
                    <span className="text-slate-300">|</span>
                    <button
                      type="button"
                      onClick={clearAllAgents}
                      className="text-xs font-medium text-slate-600 hover:underline"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
                <div className="overflow-y-auto p-2 space-y-0.5">
                  {selectableAgents.map((agent) => {
                    const id = String(agent._id);
                    const isPosted = Boolean(postedByAgent[id]);
                    return (
                      <label
                        key={id}
                        className="flex items-start gap-2 rounded-lg px-2 py-2 text-sm text-slate-800 hover:bg-slate-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 rounded border-slate-300 text-blue-900 focus:ring-blue-800 flex-shrink-0"
                          checked={Boolean(selectedMap[id])}
                          onChange={() => toggleAgent(id)}
                        />
                        <span className="leading-snug break-words min-w-0">
                          {agentLabel(agent)}
                          {isPosted ? (
                            <span className="ml-1 text-xs text-emerald-700">
                              (posted)
                            </span>
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </aside>
            ) : null}

            <div className="min-w-0 flex-1">
              {building ? (
                <div className="rounded-xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500 shadow-sm">
                  Preparing T4A
                  {selectedAgents.length > 1
                    ? ` for ${selectedAgents.length} agents`
                    : ""}
                  …
                </div>
              ) : showPdf ? (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <iframe
                    title="T4A PDF"
                    src={pdfUrl}
                    className="w-full min-h-[80vh] border-0"
                  />
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500 shadow-sm">
                  {isOfficeUser
                    ? "Select agents to preview and print their T4A slips."
                    : "No T4A to display."}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
